import {
    FormState,
    FormValues,
} from '@/components/survey/SurveyModel/types/commonTypes'
import { INode } from '@/components/survey/SurveyModel/types/nodeTypes'
import { constants } from '@/constants'
import { Parser } from 'expr-eval'
import sanitizeHtml from 'sanitize-html'

const Utility = (function () {
    const parser = new Parser()
    parser.functions.contains = (str: string, substr: string) =>
        str.includes(substr)
    parser.functions.includes = (str: string | [], val: string) => {
        if (typeof str === 'string') {
            return str.toLowerCase().includes(val.toLowerCase())
        }
        if (Array.isArray(str)) {
            return str.some(
                (item: string) =>
                    typeof item === 'string' &&
                    item.toLowerCase() === val.toLowerCase()
            )
        }

        return false // fallback for other types
    }

    const hexEncode = (str: string) => {
        let hex, i

        let result = ''
        for (i = 0; i < str.length; i++) {
            hex = str.charCodeAt(i).toString(16)
            result += ('000' + hex).slice(-4)
        }

        return result
    }

    const sanitizeContent = (content: string) => {
        return sanitizeHtml(content, {
            allowedTags: ['b', 'i', 'em', 'strong', 'p', 'ul', 'li', 'br', 'a'], // Allow only safe tags
            allowedAttributes: {
                '*': ['class'],
                a: ['href', 'target', 'rel'],
            },
            transformTags: {
                a: (tagName, attribs) => {
                    if (attribs.target === '_blank') {
                        return {
                            tagName,
                            attribs: {
                                ...attribs,
                                rel: 'noopener noreferrer',
                            },
                        }
                    }
                    return { tagName, attribs }
                },
            },
        })
    }

    const hexDecode = (str: string) => {
        let j
        const hexes = str.match(/.{1,4}/g) || []
        let back = ''
        for (j = 0; j < hexes.length; j++) {
            back += String.fromCharCode(parseInt(hexes[j], 16))
        }

        return back
    }

    const deepGet = (obj: any, keys: any) =>
        keys.reduce((xs: any, x: any) => xs?.[x] ?? null, obj)

    const getVariablePath = (variable: string | undefined) => {
        return variable ? variable.replace(/^{|}$/g, '').split('.').join() : ''
    }

    const isStringifiedArray = (value: string): boolean => {
        try {
            const parsed = JSON.parse(value)
            return Array.isArray(parsed)
        } catch {
            return false
        }
    }

    const evaluateExp = (node: INode, form: FormState): boolean => {
        let finalExpression = node.showIf
        const regex = /{(.*?)}/g

        if (finalExpression && node.showIf) {
            node.showIf.match(regex)?.forEach((element: string) => {
                const elementName = getVariablePath(element)
                const variableValue = form[elementName]?.value

                let replacement: string

                if (
                    typeof variableValue === 'string' &&
                    isStringifiedArray(variableValue)
                ) {
                    try {
                        const parsed = JSON.parse(variableValue)

                        let flattened: string[]

                        if (Array.isArray(parsed)) {
                            // Check if it's already flat (no elements are arrays)
                            const isFlat = parsed.every(
                                (item) => !Array.isArray(item)
                            )

                            flattened = isFlat
                                ? parsed.map((item: any) => item.value ?? item) // handles array of strings or objects
                                : parsed
                                      .flat()
                                      .map((item: any) => item.value ?? item)
                        } else {
                            flattened = []
                        }

                        replacement = JSON.stringify(flattened)
                    } catch {
                        replacement = '[]'
                    }
                } else if (Array.isArray(variableValue)) {
                    replacement = JSON.stringify(variableValue)
                } else if (typeof variableValue === 'string') {
                    replacement = `"${variableValue}"`
                } else {
                    replacement = String(variableValue)
                }

                finalExpression = finalExpression?.replace(element, replacement)
            })

            try {
                const evalex = parser.evaluate(finalExpression)
                console.log(evalex, 'parser.evaluate(finalExpression)')
                return Boolean(parser.evaluate(finalExpression))
            } catch (error) {
                console.error('Error evaluating expression:', finalExpression)
                console.error('Caught error:', error)
                return false // or throw error if you want it to bubble up
            }
        }

        return false
    }

    const evaluateManually = (
        form: FormState,
        exp: string,
    ) => {
        // NOTE TO SELF
        // refactored to resolve with dependencies
        // previous implementation did not account for expressions with multiple variables or complex expressions with nested dependencies
        return evaluateWithDependencies(form, exp);
    }

    const evaluateWithDependencies = (form: FormState, exp: string) => {
        let resolvedExp = exp;
        const regex = /{(.*?)}/g;
        exp.match(regex)?.forEach((variable: string) => {
            const variablePath = getVariablePath(variable)
            const value = form[variablePath]?.value as any
            if (typeof value === 'string') {
                resolvedExp = resolvedExp.replace(
                    variable,
                    `"${value as string}"`
                )
            } else if (typeof value === 'number') {
                resolvedExp = resolvedExp.replace(variable, value.toString())
            } else if (typeof value === 'boolean') {
                resolvedExp = resolvedExp.replace(
                    variable,
                    value ? 'true' : 'false'
                )
            } else if (value === undefined || value === null) {
                resolvedExp = resolvedExp.replace(variable, `""`)
            } else {
                resolvedExp = resolvedExp.replace(
                    variable,
                    JSON.stringify(value)
                )
            }
        })
        try {
            return parser.evaluate(resolvedExp);
        }
        catch (error) {
            console.error('Error evaluating expression:', resolvedExp)
            return false;
        }
    }

    const evaluateByValue = (
        variableValue: FormValues,
        finalExpression: string
    ) => {
        const regex = /{(.*?)}/g
        if (finalExpression) {
            finalExpression?.match(regex)?.forEach((element: string) => {
                if (typeof variableValue === 'string') {
                    finalExpression = finalExpression?.replace(
                        element,
                        `'${String(variableValue)}'`
                    )
                } else {
                    finalExpression = finalExpression?.replace(
                        element,
                        String(variableValue)
                    )
                }
            })
            try {
                return parser.evaluate(finalExpression)
            } catch (error) {
                console.error('Error evaluating expression:', finalExpression)
                console.error('Caught error:', error)
                return false // or throw error if you want it to bubble up
            }
        }
        return false
    }

    const getWidthFromConfig = (node: INode) => {
        if (node?.configurations) {
            const fieldWidthConfig = node?.configurations.find(
                (config) => config.key === constants.FieldWidthPct
            )
            const fieldWidthValue = fieldWidthConfig
                ? fieldWidthConfig.value
                : null

            return fieldWidthValue
        }

        return null
    }

    const getStyleFromConfig = (node: INode) => {
        if (node?.configurations) {
            const fieldWidthConfig = node?.configurations.find(
                (config) => config.key === constants.FieldStyles
            )
            const fieldWidthValue = fieldWidthConfig
                ? fieldWidthConfig.value
                : []

            return fieldWidthValue
        }

        return null
    }

    const getClassesForStyles = (node: INode) => {
        const width = Utility.getStyleFromConfig(node)
        switch (width) {
            case '128':
                return 'width-128'
            case '418':
                return 'width-418'
            default:
                return ''
        }
    }

    const getClassesForLabel = (node: INode) => {
        const classes = getValueFromKey(node, constants.LabelClasses)
        return classes ? classes : '' 
    }

    const getClassForWidth = (node: INode) => {
        const width = getWidthFromConfig(node)
        switch (width) {
            case '0.5':
                return 'width-half'
            case '0.75':
                return 'width-75'
            case '1':
                return 'width-full'
            default:
                return ''
        }
    }

    const getClassForWidthPx = (node: INode) => {
        const width = Utility.getPxWidthFromConfig(node)
        switch (width) {
            case '128':
                return 'width-128'
            case '120':
                return 'width-120'
            case '418':
                return 'width-418'
            default:
                return ''
        }
    }

    const getPxWidthFromConfig = (node: INode) => {
        if (node?.configurations) {
            const fieldWidthConfig = node?.configurations.find(
                (config) => config.key === constants.FieldWidthPx
            )
            const fieldWidthValue = fieldWidthConfig
                ? fieldWidthConfig.value
                : null

            return fieldWidthValue
        }

        return null
    }

    const getValueFromKey = (node: INode, key: string) => {
        if (node?.configurations) {
            const fieldWidthConfig = node?.configurations.find(
                (config) => config.key === key
            )

            return fieldWidthConfig?.value && fieldWidthConfig?.value
        }

        return null
    }

    /**
     * Extracts node hash from a node name by splitting on '-' and rejoining everything after the first element
     * @param nodeName - The node name to extract hash from
     * @returns The extracted node hash
     */
    const extractNodeHash = (nodeName: string, isChildWithinRepeatableElement?: boolean): string => {
        return nodeName.split('-').slice(isChildWithinRepeatableElement ? 2 : 1).join('-')
    }

    const convertHeightToImperial = (heightCM: number) => {
        const totalInches = heightCM / 2.54;
        const feet = Math.floor(totalInches / 12);
        const inches = totalInches % 12;
        return { feet, inches };
    }

    const convertWeightToImperial = (weightKG: number) => {
        const totalPounds = weightKG * 2.20462;
        const stone = Math.floor(totalPounds / 14);
        const pounds = totalPounds % 14;
        return { stone, pounds };
    }

    const findParentSection = (targetNodeName: string, nodes: INode[]): string | null => {
        if (!nodes) return null;
        
        // Remove "-node-uuid" suffix if present
        const cleanTargetNodeName = targetNodeName.replace(/-node-.*$/, '');
        
        const findNodeAndParentSection = (nodes: INode[], parentSection: string | null = null): string | null => {
            for (const node of nodes) {
                // If this is a section node, it becomes the current parent section
                const currentParentSection = node.type === 'Section' ? node.name : parentSection;
                
                // If we found the target node, return its parent section
                if (node.name === cleanTargetNodeName) {
                    return currentParentSection;
                }
                
                // Recursively search in child nodes
                if (node.nodes && node.nodes.length > 0) {
                    const result = findNodeAndParentSection(node.nodes, currentParentSection);
                    if (result) return result;
                }
                
                // Also search in repeated nodes if they exist
                if (node.repeatedNodes && node.repeatedNodes.length > 0) {
                    const result = findNodeAndParentSection(node.repeatedNodes, currentParentSection);
                    if (result) return result;
                }
            }
            return null;
        };

        return findNodeAndParentSection(nodes);
    };

    const processFieldPlaceholders = (
        expression: string,
        fieldHash: string = '',
        formData: any
    ): string => {
        let processedExpression = expression;
        const fieldMatches = expression.match(/{(\w+)}/g);
        if (fieldMatches) {
            fieldMatches.forEach((match: string) => {
                const fieldName = match.replace(/[{}]/g, '');
                const fieldWithHash = fieldHash.length > 0 ? `${fieldName}-${fieldHash}` : fieldName;
                const fieldValue = formData.getFormDataByFieldName ? formData.getFormDataByFieldName(fieldWithHash) : formData[fieldWithHash]?.value;
                processedExpression = processedExpression.replace(match, `"${fieldValue}"`);
            });
        }

        return processedExpression;
    }

    const getFlatNodeList = (node: INode, form: FormState, isRepeatedNode: boolean = false): INode[] => {
        const nodeArray: INode[] = [];
        
        if (isRepeatedNode) {
            // Find all form keys that start with the node name followed by a dash
            const repeatedNodeKeys = Object.keys(form).filter(key => 
                key.startsWith(node.name + '-')
            );
            
            // Add a node entry for each repeated instance
            repeatedNodeKeys.forEach(key => {
                const nodeWithActualName = { ...node, name: key };
                nodeArray.push(nodeWithActualName);
            });
        } else {
            nodeArray.push(node);
        }
        
        // Traverse child nodes
        if (node.nodes && node.nodes.length > 0) {
            node.nodes.forEach((childNode: INode) => {
                nodeArray.push(...getFlatNodeList(childNode, form, isRepeatedNode));
            });
        }
        
        // Traverse repeated nodes
        if (node.repeatedNodes && node.repeatedNodes.length > 0) {
            node.repeatedNodes.forEach((repeatedNode: INode) => {
                nodeArray.push(...getFlatNodeList(repeatedNode, form, true));
            });
        }
        
        return nodeArray;
    }

    return {
        evaluateManually: evaluateManually,
        getClassForWidth: getClassForWidth,
        hexEncode: hexEncode,
        hexDecode: hexDecode,
        deepGet: deepGet,
        evaluateExp: evaluateExp,
        getClassForWidthPx: getClassForWidthPx,
        getWidthFromConfig: getWidthFromConfig,
        getPxWidthFromConfig: getPxWidthFromConfig,
        sanitizeContent: sanitizeContent,
        getVariablePath: getVariablePath,
        evaluateByValue: evaluateByValue,
        getClassesForStyles: getClassesForStyles,
        getStyleFromConfig: getStyleFromConfig,
        getValueFromKey: getValueFromKey,
        convertHeightToImperial: convertHeightToImperial,
        convertWeightToImperial: convertWeightToImperial,
        getClassesForLabel: getClassesForLabel,
        findParentSection: findParentSection,
        extractNodeHash: extractNodeHash,
        processFieldPlaceholders,
        getFlatNodeList
    }
})()

export default Utility
